'use client';

import { Fragment, ReactNode, useState } from 'react';
import type { FaqItem } from '@/lib/faq-content';

interface FaqAccordionProps {
  items: FaqItem[];
  accent: string;
}

function renderWithLinks(text: string, accent: string): ReactNode {
  const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  const matches = [...text.matchAll(LINK_PATTERN)];

  for (const match of matches) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    parts.push(
      <a
        key={`${match[1]}-${match[2]}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium hover:underline transition-colors"
        style={{ color: accent }}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

export function FaqAccordion({ items, accent }: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {(items || []).map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
          >
            <button
              type="button"
              onClick={() => toggle(index)}
              aria-expanded={isOpen}
              className="w-full px-6 sm:px-8 py-5 sm:py-6 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors duration-300"
            >
              <span className="font-poppins text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 pr-4">
                {item?.question}
              </span>
              <div
                className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white transition-transform duration-500 ${
                  isOpen ? 'rotate-180' : ''
                }`}
                style={{ backgroundColor: accent }}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            <div
              className={`grid transition-all duration-500 ease-in-out ${
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-6 sm:px-8 pb-6 pt-2">
                  <div className="w-16 h-1 mb-4" style={{ backgroundColor: accent }} />
                  <div className="space-y-3 text-base sm:text-lg text-gray-600 leading-relaxed">
                    {(item?.answerParagraphs || []).map((paragraph, pIdx) => (
                      <p key={pIdx} className="whitespace-pre-line">
                        <Fragment>{renderWithLinks(paragraph, accent)}</Fragment>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
